import React, {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren,
} from 'react';
import { graphql, useFragment, type EntryPointProps } from 'react-relay';
import { SpotlightTarget, SpotlightManager } from '@atlaskit/onboarding';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ColumnPickerContainer } from '@atlassian/jira-issue-table-column-picker/src/controllers/column-picker-open-state/index.tsx';
import AsyncColumnPicker from '@atlassian/jira-issue-table-column-picker/src/ui/async.tsx';
import {
	ContextualAnalyticsData,
	FireUiAnalytics,
	SCREEN,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	HierarchyContainer,
	type PopulateConnectionArgs,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import { ConnectionsListContainer } from '@atlassian/jira-issue-table-hierarchy/src/controllers/connections-list/index.tsx';
import {
	KNOWN_COLUMN_TYPES,
	COLUMN_ID_MERGED_COLUMN,
} from '@atlassian/jira-issue-table-common/src/constants.tsx';
import type { onChangeType } from '@atlassian/jira-issue-table-column-picker/src/ui/ColumnConfigWithContainer.tsx';
import { HoverPopoverStateProvider } from '@atlassian/jira-hover-popover/src/controllers/hover-popover-state-provider/index.tsx';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import {
	BORDER_WIDTH,
	COLUMN_ID_MEATBALL_MENU,
	COLUMN_ID_EXPAND_BUTTON,
	COLUMN_TYPE_PARENT_LINK,
	DEFAULT_COLUMN_WIDTH,
	DENSITY_COMPACT,
	LAZY_EDITABILITY_LOADER_CONTAINER_SCOPE,
	LAZY_MEDIA_VIEW_CONTEXT_LOADER_CONTAINER_SCOPE,
	MODIFY_COLUMNS,
	REORDER_COLUMNS,
	SORT_ASC,
	SORT_NONE,
	DENSITY_FULL,
	COLUMN_ID_CHECKBOX,
	COLUMN_PICKER_CONFIGURATION_CONTAINER,
	ACTION_COLUMNS,
	MINIMUM_MERGED_CELL_COLUMN_WIDTH,
} from '../common/constants.tsx';
import type { Column, SortOrder } from '../common/types.tsx';
import ExpandHeaderCell from '../common/ui/header-cell/expand/index.tsx';
import { HeaderCheckboxCell } from '../common/ui/issue-cells/checkbox/index.tsx';
import { IssueTableSkeleton } from '../common/ui/skeleton/index.tsx';
import { getColumnConfig } from '../common/utils/get-column-config/index.tsx';
import { getColumnsWithStickyOffsets } from '../common/utils/get-column-config-with-sticky-offset/index.tsx';
import { isKnownColumnType, isNonNullish, desanitizeField } from '../common/utils/index.tsx';
import { ElementSizeProvider } from '../controllers/element-size/index.tsx';
import { FeaturesContainer } from '../controllers/features/container.tsx';
import { SelectedRowContainer } from '../controllers/selected-row-state/SelectedRowContainer.tsx';
import { SiblingCreateActiveIndexContainer } from '../controllers/sibling-create-active-index/index.tsx';
import { InlineCreateActiveIndexContainer } from '../controllers/inline-create-active-index/index.tsx';
import { TableIntersectionObserverContainer } from '../controllers/table-items-intersection-observer/index.tsx';
import { LazyEditabilityLoaderContainer } from '../services/lazy-editability-loader/index.tsx';
import { LazyMediaViewContextLoaderContainer } from '../services/lazy-media-view-context-loader/index.tsx';
import { DraggableRowsContainer } from '../controllers/draggable-rows/context.tsx';
import { IFCFooter } from '../common/ui/inline-field-config/column-picker-footer/index.tsx';
import { TimelineCustomComponentsProvider } from '../controllers/timeline-custom-components/index.tsx';
import { MaybeEventHandlersProvider } from '../controllers/event-handlers-context/index.tsx';
import {
	getMergedColumnConfig,
	getMergedColumnConfigWithDynamicOrder,
} from '../common/utils/get-merged-column-config/index.tsx';
import { SortChangeContextProvider } from '../controllers/sort-change-context-provider/index.tsx';
import { useKeyboardNavigation } from '../controllers/use-keyboard-navigation/use-keyboard-navigation.tsx';
import { ShouldUseSSRVirtualizerProvider } from '../controllers/virtualization/index.tsx';
import type { NativeIssueTableWithSelectedIssuesProvider_rules$key as FormattingRulesFragment } from './__generated__/NativeIssueTableWithSelectedIssuesProvider_rules.graphql';
import type { NativeIssueTableWithSelectedIssuesProvider$key as ViewFragment } from './__generated__/NativeIssueTableWithSelectedIssuesProvider.graphql';
import type { NativeIssueTableWithSelectedIssuesProvider_rankField$key as RankFieldFragment } from './__generated__/NativeIssueTableWithSelectedIssuesProvider_rankField.graphql';
import type { NativeIssueTableWithSelectedIssuesProvider_project$key as ProjectFragment } from './__generated__/NativeIssueTableWithSelectedIssuesProvider_project.graphql';
import type { NativeIssueTableWithSelectedIssuesProvider_issues$key as IssuesFragment } from './__generated__/NativeIssueTableWithSelectedIssuesProvider_issues.graphql';
import type { NativeIssueTableWithSelectedIssuesProvider_group$key as GroupsFragment } from './__generated__/NativeIssueTableWithSelectedIssuesProvider_group.graphql';
import type { NativeIssueTableWithSelectedIssuesProvider_columns$key as ColumnsFragment } from './__generated__/NativeIssueTableWithSelectedIssuesProvider_columns.graphql';
import FooterRenderer from './footer-renderer/FooterRenderer.tsx';
import { IssueFetchQueryVariables } from './issue-fetch-query-variables/IssueFetchQueryVariables.tsx';
import IssueTable from './issue-table/IssueTable.tsx';
import KeyboardShortcuts from './keyboard-shortcuts/index.tsx';
import messages from './messages.tsx';
import { RealtimeUpdater } from './realtime-updater/RealtimeUpdater.tsx';
import { ScrollContainer as DefaultScrollContainer } from './scroll-container/index.tsx';
import { MainContainer as DefaultMainContainer } from './main-container/index.tsx';
import { IssueTableSelectedIssuesProvider } from './selected-issues-provider/index.tsx';
import type { Props } from './types.tsx';
import { CopyPasteContainer } from './copy-paste-container/index.tsx';
import { ConnectionsSubscriberContainer } from './connections-subscriber-container/index.tsx';
import { RootConnectionRegister } from './root-connection-register/RootConnectionRegister.tsx';
import { IssueRealtimeUpdater } from './issue-realtime-updater/IssueRealtimeUpdater.tsx';
import { FlatListUpdateHandler } from './flat-list-update-handler/FlatListUpdateHandler.tsx';

export const MaybeInlineCreateActiveIndexContainer = componentWithCondition(
	() => fg('jira_inline_field_config_gate'),
	InlineCreateActiveIndexContainer,
	({ children }: PropsWithChildren<{}>) => children,
);

export const MaybeHoverPopoverProvider = componentWithCondition(
	() => fg('issue_table_single_line_row_height_event_delegator'),
	HoverPopoverStateProvider,
	({ children }: PropsWithChildren<{}>) => children,
);

const NativeIssueTable = ({
	view,
	issues,
	groups,
	columns,
	project,
	rankField,
	columnWidthOverrides,
	columnConfigOverrides,
	components,
	loading = false,
	selectedRow = null,
	eventHandlers,
	isInlineEditingEnabled = true,
	isInlineEditingExtendedFieldSupportEnabled = false,
	onSelectedRowChange,
	sortField,
	sortDirection = SORT_ASC,
	onReady,
	onSortChange,
	onColumnsChange,
	onColumnResize,
	onColumnFreeze,
	onRestoreDefaultColumns,
	issueSearchBaseUrl = '/issues',
	useColumnLoader,
	isColumnPickerLoading,
	projectContext,
	density = DENSITY_COMPACT,
	isIssueHierarchyEnabled = false,
	isHideDoneItemsEnabled = false,
	areShortcutsEnabled = true,
	isInlineFieldConfigEnabled = false,
	isInBetweenColumnPickerEnabled = false,
	isIssueCreateEnabled = false,
	enableMultiSelect = false,
	tableBorderRadius,
	onIssueFlatListUpdated,
	jql,
	isStatusTrackingEnabled = false,
	isMergedColumnEnabled = false,
	isChildIssuesTableVisualUpliftEnabled = false,
	isChildIssuesTable = false,
	isIssueRankingEnabled = false,
	isTimelineEnabled = false,
	isSkipIssueCreateSuccessFlagEnabled = false,
	// Discrete pagination props
	onPageChange,
	// Infinite scroll pagination props
	onLoadNext,
	onLoadPrevious,
	isLoadingNext,
	isLoadingPrevious,
	hasNext,
	hasPrevious,
	activationThreshold,
	// Experiment value props
	isGroupingSupported = false,
	isOpenIssueLinkEnabled = true,
	isIssueOperationMeatballMenuEnabled = true,
	isCompactColumnEnabled = false,
	isReparentingEnabled = false,
	isColumnBordersEnabled = true,
	isSingleLineRowHeightEnabled = false,
	isGridNavigationEnabled = false,
	// Row reordering props
	onRowReorder, // TODO: Remove on jsc-2065-unify-onrowreorder cleanup
	isEntireRowDraggable = false,
	ifcConfig,
	onRefetch,
	isStickyColumnsEnabled = false,
	isConditionalFormattingEnabled = false,
	stickyColumnIds,
	enableAutoUnstickyOnSmallScreen = false,
	enableDynamicOrderForMergedColumn = false,
	cellBorderStyle = 'FULL_BORDER',
	isProjectQuickstartOnboardingEnabled = false,
	isLinkRelationshipFieldEnabled = false,
	isRemoteLinkedIssuesEnabled = false,
	isEditableSyncLoaded = false,
	isDraggingRowFaded = true,
	footerInlineCreateCanShowAdditionalFields = true,
	rowInlineCreateCanShowAdditionalFields = true,
	mergedColumnMinWidth = fg('refactor_issue_table_for_timline')
		? MINIMUM_MERGED_CELL_COLUMN_WIDTH
		: undefined,
	tableRole,
	formattingRules,
	footerBorderRadius,
	resizeHandleType,
	isInBetweenStickyColumnPickerEnabled = false,
	isColumnConfigColumnEnabled = true,
	isRemoveColumnEnabled = true,
	topLevelIssueCreateConfig,
	isMakeSpaceForGlobalRovoButtonEnabled = false,
}: Props) => {
	const tableRef = useRef<HTMLTableElement>(null);
	const { formatMessage } = useIntl();
	if (isGridNavigationEnabled) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useKeyboardNavigation(tableRef, isStickyColumnsEnabled);
	}
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const viewData = useFragment<ViewFragment>(
		graphql`
			fragment NativeIssueTableWithSelectedIssuesProvider on JiraIssueSearchViewMetadata {
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
				...ColumnConfigWithContainer_savedViewId
				...IssueTableIssueTable
			}
		`,
		view ?? null,
	);

	const issuesData = useFragment<IssuesFragment>(
		graphql`
			fragment NativeIssueTableWithSelectedIssuesProvider_issues on JiraIssueConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
				isReparentingEnabled: { type: "Boolean", defaultValue: false }
				isTimelineEnabled: { type: "Boolean", defaultValue: false }
				pageSize: { type: "Int" }
				isDensityFull: { type: "Boolean", defaultValue: false }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean", defaultValue: false }
				isConditionalFormattingEnabled: { type: "Boolean", defaultValue: false }
				isIssueRealtimeExtractionEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-issue-realtime-extraction.relayprovider"
				}
				isLinkRelationshipFieldEnabled: { type: "Boolean", defaultValue: false }
				isRemoteLinkedIssuesEnabled: { type: "Boolean", defaultValue: false }
				isEditableSyncLoaded: { type: "Boolean", defaultValue: false }
				isBulkOpsSelectAllEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/bulk-ops-select-all.relayprovider"
				}
				isInitialiseVirtualiserStateFromRelayStoreEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/initialise-virtualiser-state-from-relay-store.relayprovider"
				}
			) {
				__id
				edges {
					__typename
					node @include(if: $isInitialiseVirtualiserStateFromRelayStoreEnabled) {
						issueId
						key
					}
				}
				jql
				...IssueTable_issueConnection
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isReparentingEnabled: $isReparentingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isDensityFull: $isDensityFull
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...FooterRenderer @arguments(pageSize: $pageSize)
				...SelectedRowContainer @arguments(pageSize: $pageSize)
				...RealtimeUpdater
				...IssueRealtimeUpdater @include(if: $isIssueRealtimeExtractionEnabled)
				...RootConnectionRegister
				totalIssueSearchResultCount @include(if: $isBulkOpsSelectAllEnabled)
			}
		`,
		issues ?? null,
	);

	const groupData = useFragment<GroupsFragment>(
		graphql`
			fragment NativeIssueTableWithSelectedIssuesProvider_group on JiraSpreadsheetGroupConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
				isTimelineEnabled: { type: "Boolean", defaultValue: false }
				isDensityFull: { type: "Boolean", defaultValue: false }
				isPaginating: { type: "Boolean", defaultValue: false }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				viewConfigInput: { type: "JiraIssueSearchViewConfigInput" }
				isConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
				isIssueRealtimeExtractionEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-issue-realtime-extraction.relayprovider"
				}
				isLinkRelationshipFieldEnabled: { type: "Boolean", defaultValue: false }
				isRemoteLinkedIssuesEnabled: { type: "Boolean", defaultValue: false }
				isEditableSyncLoaded: { type: "Boolean", defaultValue: false }
			) {
				__id
				groupByField
				jql
				...IssueTable_spreadsheetGroupConnection
					@arguments(
						isInlineEditingEnabled: $isInlineEditingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isDensityFull: $isDensityFull
						isPaginating: $isPaginating
						projectKeys: $projectKeys
						projectKey: $projectKey
						viewConfigInput: $viewConfigInput
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
						isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
						isEditableSyncLoaded: $isEditableSyncLoaded
					)
				...RealtimeUpdater_spreadsheetGroupConnection
				...IssueRealtimeUpdater_spreadsheetGroupConnection
					@include(if: $isIssueRealtimeExtractionEnabled)
			}
		`,
		groups ?? null,
	);

	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	const columnsData = useFragment<ColumnsFragment>(
		graphql`
			fragment NativeIssueTableWithSelectedIssuesProvider_columns on JiraIssueSearchFieldSetConnection
			@argumentDefinitions(
				isJscIFCEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-jsc-ifc-enabled.relayprovider"
				}
				isJSCAliasChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-alias-changes.relayprovider"
				}
			) {
				...IssueFetchQueryVariables
				totalCount @required(action: THROW)
				edges @required(action: THROW) {
					node @required(action: THROW) {
						fieldSetId @required(action: THROW)
						type @required(action: THROW)
						displayName @required(action: THROW)
						jqlTerm @required(action: THROW)
						aliases @include(if: $isJSCAliasChangesEnabled)
						isSortable
						fieldSetPreferences {
							width
						}
						isPlaceHolderField
						fieldsMetadata @include(if: $isJscIFCEnabled) {
							edges {
								node {
									canBeConfigured
									fieldId
								}
							}
						}
					}
				}
				...ColumnConfigWithContainer
				...context_nativeIssueTable_IFCConfigContextProvider_columnsQueryRef
			}
		`,
		columns,
	);
	/* eslint-enable @atlassian/relay/must-colocate-fragment-spreads */

	const projectData = useFragment<ProjectFragment>(
		graphql`
			fragment NativeIssueTableWithSelectedIssuesProvider_project on JiraProject
			@argumentDefinitions(
				isReparentingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-list-reparenting.relayprovider"
				}
				isConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
				isIssueRealtimeExtractionEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-issue-realtime-extraction.relayprovider"
				}
			) {
				editIssues: action(type: EDIT_ISSUES) @include(if: $isReparentingEnabled) {
					canPerform
				}
				createIssues: action(type: CREATE_ISSUES) {
					canPerform
				}
				...RealtimeUpdater_project
				...IssueRealtimeUpdater_project @include(if: $isIssueRealtimeExtractionEnabled)
				...IssueTable_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project ?? null,
	);

	const rankFieldData = useFragment<RankFieldFragment>(
		graphql`
			fragment NativeIssueTableWithSelectedIssuesProvider_rankField on JiraJqlFieldWithAliases {
				...IssueTable_rankField
				...FooterRenderer_rank
			}
		`,
		rankField ?? null,
	);

	const formattingRulesData = useFragment<FormattingRulesFragment>(
		graphql`
			fragment NativeIssueTableWithSelectedIssuesProvider_rules on JiraFormattingRuleConnection {
				...IssueTable_rules
			}
		`,
		formattingRules ?? null,
	);

	const isInitialDataLoaded = useRef(false);

	useEffect(() => {
		if (!loading && !isInitialDataLoaded.current) {
			isInitialDataLoaded.current = true;
		}
	}, [loading]);

	// initialPopulateConnectionArgs is used to initialise the hierarchy store container on initial load for root connections.
	const [initialisePopulateConnectionArgs] = useState<PopulateConnectionArgs[] | undefined>(() => {
		if (!fg('initialise_virtualiser_state_from_relay_store')) {
			return undefined;
		}

		const connections: PopulateConnectionArgs[] = [];
		// Rows.tsx pattern with root parent
		if (issuesData && issuesData.edges) {
			const issueItems =
				issuesData.edges
					.map(
						(edge) =>
							edge?.node && {
								key: edge.node.key,
								itemId: edge.node.issueId,
								groupId: undefined,
							},
					)
					.filter(Boolean) ?? [];

			connections.push({
				parentItemId: 'root',
				itemsConnectionId: issuesData.__id,
				parentConnectionId: issuesData.__id,
				list: issueItems,
			});

			return connections;
		}

		return undefined;
	});

	const analyticsAttributes = useMemo(() => {
		const issueTableNumberOfColumns = columnsData.totalCount;

		if (fg('add-fields-enabled-attribute-to-nin-screen-event')) {
			const issueTableFieldTypes = columnsData.edges.map((edge) => edge?.node.type);
			const issueTableFieldTypesWithCount: Record<string, number> = issueTableFieldTypes
				.filter(isNonNullish)
				.sort()
				.reduce((acc: Record<string, number>, type) => {
					acc[type] = (acc[type] || 0) + 1;
					return acc;
				}, {});

			return {
				issueTableNumberOfColumns,
				issueTableFieldTypes,
				issueTableFieldTypesWithCount,
			};
		}
		const issueTableFieldTypesWithCount: Record<string, number> = columnsData.edges
			.map((edge) => edge?.node.type)
			.filter(isNonNullish)
			.sort()
			.reduce((acc: Record<string, number>, type) => {
				acc[type] = (acc[type] || 0) + 1;
				return acc;
			}, {});
		const issueTableFieldTypes = Object.keys(issueTableFieldTypesWithCount);

		return {
			issueTableNumberOfColumns,
			issueTableFieldTypes,
			issueTableFieldTypesWithCount,
		};
	}, [columnsData.edges, columnsData.totalCount]);

	const canViewColumnConfiguration = useColumnLoader !== undefined;
	const canEditColumnConfiguration = onColumnsChange !== undefined;
	const isColumnResizingEnabled = onColumnResize != null;
	// TODO: Remove on jsc-2065-unify-onrowreorder cleanup
	const isRowReorderingEnabled = onRowReorder != null;
	const isInfiniteScrollEnabled = onLoadNext != null && onLoadPrevious != null;
	const userHasEditIssuePermission = projectData?.editIssues?.canPerform === true;
	const userHasCreateIssuePermission = projectData?.createIssues?.canPerform === true;

	if (isInfiniteScrollEnabled && density === DENSITY_FULL) {
		throw new Error(`Infinite scroll is not allowed when density=${density}`);
	}

	const onColumnPickerChange: onChangeType = useCallback(
		(pickerArgs) => {
			if (Array.isArray(pickerArgs)) {
				onColumnsChange && onColumnsChange(pickerArgs, MODIFY_COLUMNS);
			} else if (fg('jira_inline_field_config_gate')) {
				const {
					columnIds,
					isHorizontalAutoScrollNeeded,
					isHorizontalAutoScrollLeftNeeded,
					onSuccessCallback,
					onErrorCallback,
					retries,
					analyticsAttributes: attributes,
					skipRefetch = false,
				} = pickerArgs;

				if (
					isHorizontalAutoScrollLeftNeeded &&
					isMergedColumnEnabled &&
					fg('jira_list_merged_column_gate')
				) {
					tableRef?.current?.parentElement?.scrollTo({
						left: 0,
						behavior: 'smooth',
					});
				} else if (isHorizontalAutoScrollNeeded) {
					tableRef?.current?.parentElement?.scrollTo({
						left: tableRef?.current.scrollWidth,
						behavior: 'smooth',
					});
				}

				onColumnsChange &&
					onColumnsChange(
						columnIds,
						MODIFY_COLUMNS,
						undefined,
						onSuccessCallback,
						onErrorCallback,
						retries,
						attributes,
						skipRefetch,
					);
			}
		},
		[isMergedColumnEnabled, onColumnsChange],
	);

	const isMergedColumnVisible = useMemo(() => {
		if (isMergedColumnEnabled && fg('jira_list_merged_column_gate')) {
			return columnsData.edges.some(
				(edge) =>
					edge?.node?.type === KNOWN_COLUMN_TYPES.ISSUE_KEY ||
					edge?.node?.type === KNOWN_COLUMN_TYPES.ISSUE_TYPE ||
					edge?.node?.type === KNOWN_COLUMN_TYPES.SUMMARY,
			);
		}

		return false;
	}, [isMergedColumnEnabled, columnsData]);

	const mappedColumnConfig = useMemo(() => {
		const columnConfig = getColumnConfig(formatMessage);

		const columnsWithConfig: Array<Column> = [];

		const types = columnsData.edges.filter(isNonNullish).map((edge) => edge.node.type);

		if (isIssueHierarchyEnabled && !(isMergedColumnEnabled && fg('jira_list_merged_column_gate'))) {
			columnsWithConfig.push({
				id: COLUMN_ID_EXPAND_BUTTON,
				type: KNOWN_COLUMN_TYPES.EXPAND_BUTTON,
				title: '',
				fieldTypeDisplayName: '',
				jqlTerm: '',
				aliases: [],
				customHeader: (
					<SpotlightManager>
						<SpotlightTarget name="expand-column-header">
							<ExpandHeaderCell />
						</SpotlightTarget>
					</SpotlightManager>
				),
				...columnConfig[KNOWN_COLUMN_TYPES.EXPAND_BUTTON](),
				...columnConfigOverrides?.[KNOWN_COLUMN_TYPES.EXPAND_BUTTON](types),
			});
		}

		const unsanitizedSortField = fg('jsc_list_reranking_ast_refactor')
			? sortField && desanitizeField(sortField)
			: null;

		columnsData.edges
			.map((edge) => edge?.node)
			.filter(isNonNullish)
			.forEach((node) => {
				const {
					fieldSetId,
					type,
					displayName,
					jqlTerm,
					aliases,
					isSortable,
					fieldSetPreferences,
					isPlaceHolderField,
					fieldsMetadata,
				} = node;

				let configureableFieldId;
				if (fieldsMetadata && fg('jira_inline_field_config_gate')) {
					const configureableField = fieldsMetadata?.edges?.find(
						(edge) => edge?.node?.canBeConfigured,
					);
					if (configureableField != null) {
						configureableFieldId = configureableField?.node?.fieldId;
					}
				}

				const mutableAliases: string[] = [...(aliases ?? [])];
				let currentSorting: SortOrder = SORT_NONE;
				if (fg('jsc_list_reranking_ast_refactor')) {
					if (
						unsanitizedSortField &&
						(jqlTerm.toLowerCase() === unsanitizedSortField ||
							mutableAliases.includes(unsanitizedSortField))
					) {
						currentSorting = sortDirection;
					}
				} else if (jqlTerm === sortField) {
					currentSorting = sortDirection;
				}

				const baseConfig = {
					id: fieldSetId,
					type,
					title: displayName ?? '',
					jqlTerm,
					aliases: mutableAliases,
					fieldTypeDisplayName: '',
					isSortable: isSortable ?? undefined,
					currentSorting,
					onSortOrderChanged: onSortChange,
					customWidth:
						columnWidthOverrides?.[fieldSetId] ?? fieldSetPreferences?.width ?? undefined,
					...(type === COLUMN_TYPE_PARENT_LINK && {
						warningText: formatMessage(messages.deprecatedParentLinkMessage, {
							strong: (chunks: ReactNode) => (
								<Text color="color.text.inverse" as="strong">
									{chunks}
								</Text>
							),
						}),
					}),
					configureableFieldId,
				};

				if (isKnownColumnType(type)) {
					const getColumnTypeConfig = columnConfig[type];
					const knownColumnTypeConfig = getColumnTypeConfig();
					const getColumnConfigOverride = columnConfigOverrides?.[type];
					const columnConfigOverride = getColumnConfigOverride?.(types);
					columnsWithConfig.push({
						...baseConfig,
						...knownColumnTypeConfig,
						...columnConfigOverride,
						isSortable: knownColumnTypeConfig.isSortable ?? true,
						isPlaceHolderField: !!isPlaceHolderField,
					});
				} else {
					columnsWithConfig.push({
						...baseConfig,
						defaultWidth: DEFAULT_COLUMN_WIDTH,
						isPlaceHolderField: !!isPlaceHolderField,
					});
				}
			});

		const shouldShowColumnConfigColumn = fg('jira_timeline_sticky_assignee_status_columns')
			? (canViewColumnConfiguration && isColumnConfigColumnEnabled) ||
				isIssueOperationMeatballMenuEnabled
			: canViewColumnConfiguration || isIssueOperationMeatballMenuEnabled;

		if (shouldShowColumnConfigColumn) {
			const emptyFooter = fg('slv-117-nin-column-picker-style-updates')
				? {}
				: { Footer: () => <></> };

			columnsWithConfig.push({
				id: COLUMN_ID_MEATBALL_MENU,
				type: KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS,
				title: '',
				fieldTypeDisplayName: '',
				jqlTerm: '',
				customHeader: canViewColumnConfiguration ? (
					<Inline alignInline="end">
						<AsyncColumnPicker
							view={viewData}
							columns={columnsData}
							components={
								fg('jira_inline_field_config_gate')
									? {
											...components?.columnPicker,
											...(isInlineFieldConfigEnabled ? { Footer: IFCFooter } : emptyFooter),
										}
									: components?.columnPicker
							}
							onChange={canEditColumnConfiguration ? onColumnPickerChange : undefined}
							onRestoreDefaultColumns={onRestoreDefaultColumns}
							useColumnLoader={useColumnLoader}
							isLoading={isColumnPickerLoading || loading}
							isMergedColumnEnabled={isMergedColumnEnabled && fg('jira_list_merged_column_gate')}
							isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
							projectContext={fg('jira_inline_field_config_gate') ? projectContext : undefined}
						/>
					</Inline>
				) : (
					<ScreenReaderText>{formatMessage(messages.columnHeaderIssueActions)}</ScreenReaderText>
				),
				...columnConfig[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS](),
				...columnConfigOverrides?.[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS]?.(types),
			});
		}

		return columnsWithConfig;
	}, [
		formatMessage,
		columnsData,
		isIssueHierarchyEnabled,
		isMergedColumnEnabled,
		canViewColumnConfiguration,
		isIssueOperationMeatballMenuEnabled,
		columnConfigOverrides,
		sortField,
		onSortChange,
		columnWidthOverrides,
		sortDirection,
		components?.columnPicker,
		isInlineFieldConfigEnabled,
		canEditColumnConfiguration,
		onColumnPickerChange,
		onRestoreDefaultColumns,
		useColumnLoader,
		isColumnPickerLoading,
		loading,
		projectContext,
		viewData,
		isColumnConfigColumnEnabled,
	]);

	const checkboxColumnConfig = useMemo(() => {
		const checkboxConfig = getColumnConfig(formatMessage)[KNOWN_COLUMN_TYPES.CHECKBOX]();

		return {
			id: COLUMN_ID_CHECKBOX,
			type: KNOWN_COLUMN_TYPES.CHECKBOX,
			title: '',
			fieldTypeDisplayName: '',
			jqlTerm: '',
			customHeader: (
				<HeaderCheckboxCell
					groupByFieldId={groupData?.groupByField ?? null}
					totalIssueSearchResultCount={
						expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
							? (issuesData?.totalIssueSearchResultCount ?? null)
							: undefined
					}
				/>
			),
			...checkboxConfig,
		};
	}, [formatMessage, groupData?.groupByField, issuesData?.totalIssueSearchResultCount]);

	const mappedColumns = useMemo(() => {
		// Step 1: get columnConfigs based on bulk ops gate (when enabled)
		let computedMappedColumns = mappedColumnConfig;
		if (enableMultiSelect) {
			computedMappedColumns = [checkboxColumnConfig, ...mappedColumnConfig];
		}
		// Step 2: get columnConfigs with merged column when enabled.
		if (isMergedColumnEnabled && fg('jira_list_merged_column_gate')) {
			if (fg('jira-linked-work-items-targeting-gate')) {
				computedMappedColumns = getMergedColumnConfigWithDynamicOrder(
					computedMappedColumns,
					isIssueHierarchyEnabled,
					formatMessage,
					enableDynamicOrderForMergedColumn,
				);
			} else {
				computedMappedColumns = getMergedColumnConfig(
					computedMappedColumns,
					isIssueHierarchyEnabled,
					formatMessage,
				);
			}
		}
		// Step 3: get columnConfigs with sticky attributes when frozen columns enabled.
		if (isStickyColumnsEnabled && fg('issue_table_frozen_columns')) {
			computedMappedColumns = getColumnsWithStickyOffsets(computedMappedColumns, stickyColumnIds);
		}
		return computedMappedColumns;
	}, [
		checkboxColumnConfig,
		enableMultiSelect,
		formatMessage,
		isMergedColumnEnabled,
		isIssueHierarchyEnabled,
		isStickyColumnsEnabled,
		mappedColumnConfig,
		stickyColumnIds,
		enableDynamicOrderForMergedColumn,
	]);

	let isStickyColumnsVisible = false;
	if (fg('issue_table_frozen_columns')) {
		const lastStickyColumn = mappedColumns.find((col) => col.isLastStickyColumn);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isStickyColumnsVisible = useMemo(() => {
			if (!isStickyColumnsEnabled) return false;
			if (!lastStickyColumn) return false;
			// Check if lastStickyColumn's id is not COLUMN_ID_CHECKBOX or COLUMN_ID_EXPAND_BUTTON
			return ![COLUMN_ID_CHECKBOX, COLUMN_ID_EXPAND_BUTTON].includes(lastStickyColumn.id);
		}, [lastStickyColumn, isStickyColumnsEnabled]);
	}

	const handleOnColumnsChange = useCallback(
		(newColumns: Column[]) => {
			if (onColumnsChange) {
				let columnsWithIssueData = newColumns.filter(({ id }) =>
					id ? !ACTION_COLUMNS.includes(id) : id !== COLUMN_ID_MEATBALL_MENU,
				);

				// make sure merged fields (key, type, summary) are passed to the onColumnsChange
				if (isMergedColumnVisible && fg('jira_list_merged_column_gate')) {
					// If re-ordering is enabled for merged column then update columns before merged column with correct order
					if (enableDynamicOrderForMergedColumn && fg('jira-linked-work-items-targeting-gate')) {
						const mergedColumnIndex = newColumns.findIndex(
							(col) => col?.id === COLUMN_ID_MERGED_COLUMN,
						);

						if (mergedColumnIndex !== -1) {
							const columnsOnLeft = newColumns
								.slice(0, mergedColumnIndex)
								.filter(({ id }) =>
									id ? !ACTION_COLUMNS.includes(id) : id !== COLUMN_ID_MEATBALL_MENU,
								);

							const columnsOnRight = newColumns
								.slice(mergedColumnIndex + 1)
								.filter(({ id }) =>
									id ? !ACTION_COLUMNS.includes(id) : id !== COLUMN_ID_MEATBALL_MENU,
								);

							const mergedColumns = newColumns[mergedColumnIndex].mergedColumns ?? [];
							columnsWithIssueData = [...columnsOnLeft, ...mergedColumns, ...columnsOnRight];
						}
					} else {
						const mergedColumns = newColumns.find(
							(col) => col?.id === COLUMN_ID_MERGED_COLUMN,
						)?.mergedColumns;
						if (mergedColumns?.length) {
							columnsWithIssueData = [...mergedColumns, ...columnsWithIssueData];
						}
					}
				}

				const columnIds = columnsWithIssueData.map(({ id }) => id);

				const edges = columnsWithIssueData.map(
					({
						id,
						type,
						title,
						jqlTerm,
						aliases,
						isSortable,
						fieldTypeDisplayName,
						customWidth,
					}) => ({
						node: {
							fieldSetId: id,
							type: type ?? null,
							displayName: title,
							jqlTerm: jqlTerm ?? null,
							aliases: aliases ?? [],
							isSortable: isSortable ?? null,
							fieldType: fieldTypeDisplayName
								? {
										displayName: fieldTypeDisplayName,
									}
								: null,
							fieldSetPreferences: customWidth
								? {
										width: customWidth,
									}
								: null,
							fieldsMetadata: null,
						},
					}),
				);

				const optimisticFieldSets = {
					totalCount: columnsData.totalCount,
					edges,
				};

				onColumnsChange(columnIds, REORDER_COLUMNS, optimisticFieldSets);
			}
		},
		[
			columnsData.totalCount,
			isMergedColumnVisible,
			onColumnsChange,
			enableDynamicOrderForMergedColumn,
		],
	);

	const tableInjectedProps = useMemo(
		() => ({
			...eventHandlers,
			issueSearchBaseUrl,
		}),
		[eventHandlers, issueSearchBaseUrl],
	);

	const columnPickerProps = useMemo(
		() =>
			isInBetweenColumnPickerEnabled && canViewColumnConfiguration
				? {
						columns: columnsData,
						components: components?.columnPicker,
						onChange: canEditColumnConfiguration ? onColumnPickerChange : undefined,
						onRestoreDefaultColumns,
						useColumnLoader,
						isLoading: isColumnPickerLoading || loading,
					}
				: undefined,
		[
			canEditColumnConfiguration,
			canViewColumnConfiguration,
			columnsData,
			components?.columnPicker,
			isColumnPickerLoading,
			isInBetweenColumnPickerEnabled,
			loading,
			onColumnPickerChange,
			onRestoreDefaultColumns,
			useColumnLoader,
		],
	);

	addUFOCustomData({
		numberOfColumns: mappedColumns.length,
	});

	useEffect(() => {
		onReady && fg('endeavour_nin_filter_results_gadget') && onReady();
	}, [onReady]);

	useEffect(() => {
		if (isChildIssuesTable && fg('ken-2879_is_child_issue_table')) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'columnsCount updated', 'childIssuesTable', {
				numberOfColumns: mappedColumns.length,
			});
		}
	}, [createAnalyticsEvent, isChildIssuesTable, mappedColumns.length]);

	const isPageHydrating = useIsHydrateRenderMode();

	const conditionalOnColumnsChange = canEditColumnConfiguration ? handleOnColumnsChange : undefined;

	// Show the skeleton if we are loading on initial render as we may only have partial row data
	if (loading && !isInitialDataLoaded.current) {
		return <IssueTableSkeleton />;
	}

	const footer = issuesData ? (
		<FooterRenderer
			topLevelIssueCreateConfig={topLevelIssueCreateConfig}
			Footer={components?.Footer}
			rankField={rankFieldData ?? null}
			IssueCount={components?.IssueCount}
			issues={issuesData}
			onPageChange={onPageChange}
			{...(fg('refactor_issue_table_for_timline') && {
				canShowAdditionalFields: footerInlineCreateCanShowAdditionalFields,
				footerBorderRadius,
			})}
			isMakeSpaceForGlobalRovoButtonEnabled={isMakeSpaceForGlobalRovoButtonEnabled}
		/>
	) : null;

	const connectionsSubscriber = components?.ConnectionsSubscriber && (
		<ConnectionsSubscriberContainer ConnectionsSubscriber={components.ConnectionsSubscriber} />
	);

	const hierarchyRemountKey = `${isIssueHierarchyEnabled}-${
		isGroupingSupported ? groupData?.jql || issuesData?.jql : ''
	}-${groupData?.groupByField}`;

	const ScrollContainer = components?.ScrollContainer ?? DefaultScrollContainer;

	const MainContainer = fg('jira_timeline_harmonisation_m1_targeting')
		? (components?.MainContainer ?? DefaultMainContainer)
		: DefaultMainContainer;

	return (
		<ContextualAnalyticsData
			sourceName="nativeIssueTable"
			sourceType={SCREEN}
			attributes={analyticsAttributes}
		>
			<FeaturesContainer
				density={density}
				isColumnResizingEnabled={isColumnResizingEnabled}
				isInfiniteScrollEnabled={isInfiniteScrollEnabled}
				isInlineEditingEnabled={isInlineEditingEnabled}
				isInlineEditingExtendedFieldSupportEnabled={isInlineEditingExtendedFieldSupportEnabled}
				isIssueCreateEnabled={isIssueCreateEnabled && userHasCreateIssuePermission}
				projectContext={projectContext}
				isIssueHierarchyEnabled={isIssueHierarchyEnabled}
				isIssueRankingEnabled={isIssueRankingEnabled}
				isIssueOperationMeatballMenuEnabled={isIssueOperationMeatballMenuEnabled}
				isRowReorderingEnabled={fg('jsc-2065-unify-onrowreorder') ? false : isRowReorderingEnabled}
				isEntireRowDraggable={
					fg('jsc-2065-unify-onrowreorder')
						? isEntireRowDraggable
						: isRowReorderingEnabled && isEntireRowDraggable
				}
				isReparentingEnabled={
					isReparentingEnabled &&
					isIssueHierarchyEnabled &&
					(!groupData || !groupData?.groupByField) &&
					userHasEditIssuePermission
				}
				sortDirection={sortDirection}
				sortField={sortField}
				jql={jql ?? ''}
				isColumnBordersEnabled={isColumnBordersEnabled}
				isGridNavigationEnabled={isGridNavigationEnabled}
				isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
				isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
				isSingleLineRowHeightEnabled={isSingleLineRowHeightEnabled}
				isHideDoneItemsEnabled={isHideDoneItemsEnabled}
				isMergedColumnEnabled={isMergedColumnEnabled}
				isMergedColumnVisible={isMergedColumnVisible}
				isStickyColumnsEnabled={isStickyColumnsEnabled}
				isStickyColumnsVisible={isStickyColumnsVisible}
				isAutoUnstickyOnSmallScreenEnabled={enableAutoUnstickyOnSmallScreen}
				isConditionalFormattingEnabled={
					fg('jira_list_conditional_formatting') ? isConditionalFormattingEnabled : undefined
				}
				isTimelineEnabled={isTimelineEnabled}
				isOpenIssueLinkEnabled={isOpenIssueLinkEnabled}
				isChildIssuesTableVisualUpliftEnabled={isChildIssuesTableVisualUpliftEnabled}
				cellBorderStyle={fg('jira_timeline_cell_border_style') ? cellBorderStyle : 'FULL_BORDER'}
				isProjectQuickstartOnboardingEnabled={isProjectQuickstartOnboardingEnabled}
				isLinkRelationshipFieldEnabled={isLinkRelationshipFieldEnabled}
				isRemoteLinkedIssuesEnabled={isRemoteLinkedIssuesEnabled}
				isEditableSyncLoaded={isEditableSyncLoaded}
				isSkipIssueCreateSuccessFlagEnabled={isSkipIssueCreateSuccessFlagEnabled}
				isInBetweenStickyColumnPickerEnabled={isInBetweenStickyColumnPickerEnabled}
				isColumnConfigColumnEnabled={isColumnConfigColumnEnabled}
				isRemoveColumnEnabled={isRemoveColumnEnabled}
			>
				<MaybeEventHandlersProvider eventHandlers={eventHandlers ?? {}}>
					<TimelineCustomComponentsProvider components={components?.timeline}>
						<TableIntersectionObserverContainer>
							<IssueFetchQueryVariables columns={columnsData}>
								<MaybeInlineCreateActiveIndexContainer>
									<SiblingCreateActiveIndexContainer>
										<ShouldUseSSRVirtualizerProvider>
											<HierarchyContainer
												// Key used to reset the container store when the hierarchy settings change
												// Without it there may be flickering when Concurrent Mode is enabled
												key={hierarchyRemountKey}
												{...(fg('initialise_virtualiser_state_from_relay_store') && {
													populateConnectionArgs: initialisePopulateConnectionArgs,
												})}
												// Used to determine the selected row when returning from the full page issue app. Clean up with issue app removal
												{...(fg('jsc_highlight_selected_row') && {
													selectedRowIndex: selectedRow,
												})}
											>
												<MaybeHoverPopoverProvider>
													<DraggableRowsContainer
														issueConnectionId={issuesData?.__id || ''}
														instanceId={issuesData?.__id}
														{...(fg('jsc-2065-unify-onrowreorder') ? {} : { onRowReorder })}
													>
														<MainContainer
															tableBorderRadius={
																isChildIssuesTableVisualUpliftEnabled &&
																fg('fg_visual_uplift_for_child_issues_table')
																	? token('space.0')
																	: tableBorderRadius
															}
															tableBorderWidth={
																isChildIssuesTableVisualUpliftEnabled &&
																fg('fg_visual_uplift_for_child_issues_table')
																	? '0px'
																	: BORDER_WIDTH
															}
															data-vc={`issue-table-main-container${
																__SERVER__ && !isPageHydrating ? '-ssr' : ''
															}`}
															{...(__SERVER__ &&
																!isPageHydrating && {
																	'data-ssr-placeholder': 'issue-table-main-container-placeholder',
																})}
															{...(!__SERVER__ &&
																!isPageHydrating && {
																	'data-ssr-placeholder-replace':
																		'issue-table-main-container-placeholder',
																})}
														>
															<ElementSizeProvider elementRef={tableRef}>
																<ConnectionsListContainer
																	// Key used to reset the container store when the hierarchy settings change
																	// Without it there may be flickering when Concurrent Mode is enabled
																	key={hierarchyRemountKey}
																>
																	{connectionsSubscriber}
																	<SortChangeContextProvider value={{ onSortChange }}>
																		<ScrollContainer
																			footer={footer}
																			onLoadNext={onLoadNext}
																			onLoadPrevious={onLoadPrevious}
																			isLoadingNext={isLoadingNext}
																			isLoadingPrevious={isLoadingPrevious}
																			hasNext={hasNext}
																			hasPrevious={hasPrevious}
																			activationThreshold={activationThreshold}
																			loading={loading}
																			isGrouping={isGroupingSupported && !!groupData}
																			{...(fg('issue_view_layout_experiment_feature_gate') && {
																				columnCount: mappedColumns.length,
																			})}
																			{...((fg('ken-2879_is_child_issue_table') ||
																				fg('issue_view_layout_experiment_feature_gate')) && {
																				isChildIssuesTable,
																			})}
																		>
																			<LazyEditabilityLoaderContainer
																				scope={LAZY_EDITABILITY_LOADER_CONTAINER_SCOPE}
																			>
																				<LazyMediaViewContextLoaderContainer
																					scope={LAZY_MEDIA_VIEW_CONTEXT_LOADER_CONTAINER_SCOPE}
																				>
																					<SelectedRowContainer
																						selectedRowIndex={selectedRow}
																						amountOfRows={issuesData?.edges?.length ?? 0}
																						onRowChange={onSelectedRowChange}
																						onPageChange={onPageChange}
																						issues={issuesData ?? null}
																					>
																						{areShortcutsEnabled && (
																							<KeyboardShortcuts
																								onIssueLinkSelected={
																									tableInjectedProps.onIssueLinkSelected
																								}
																							/>
																						)}
																						<ColumnPickerContainer
																							{...(isInBetweenColumnPickerEnabled && {
																								scope: COLUMN_PICKER_CONFIGURATION_CONTAINER,
																							})}
																						>
																							<CopyPasteContainer>
																								<IssueTable
																									view={viewData ?? null}
																									columnPickerProps={columnPickerProps}
																									issueResults={issuesData ?? null}
																									groupResults={groupData ?? null}
																									project={projectData ?? null}
																									rankField={rankFieldData ?? null}
																									columns={mappedColumns}
																									canViewColumnConfiguration={
																										canViewColumnConfiguration
																									}
																									canEditColumnConfiguration={
																										canEditColumnConfiguration
																									}
																									loading={loading}
																									tableInjectedProps={tableInjectedProps}
																									onColumnsChange={conditionalOnColumnsChange}
																									onResizeColumn={onColumnResize}
																									onFreezeColumn={onColumnFreeze}
																									NoColumns={components?.NoColumns}
																									NoIssues={components?.NoIssues}
																									tableRef={tableRef}
																									isGroupingSupported={isGroupingSupported}
																									isCompactColumnEnabled={isCompactColumnEnabled}
																									tableBorderRadius={
																										isChildIssuesTableVisualUpliftEnabled &&
																										fg('fg_visual_uplift_for_child_issues_table')
																											? token('space.0')
																											: tableBorderRadius
																									}
																									onIssueFlatListUpdated={onIssueFlatListUpdated}
																									ifcConfig={ifcConfig}
																									columnsQueryRef={columnsData}
																									enableDynamicOrderForMergedColumn={
																										enableDynamicOrderForMergedColumn
																									}
																									isDraggingRowFaded={isDraggingRowFaded}
																									mergedColumnMinWidth={mergedColumnMinWidth}
																									tableRole={tableRole}
																									formattingRules={formattingRulesData}
																									rowInlineCreateCanShowAdditionalFields={
																										rowInlineCreateCanShowAdditionalFields
																									}
																									resizeHandleType={resizeHandleType}
																									topLevelIssueCreateConfig={
																										topLevelIssueCreateConfig
																									}
																									{...(fg(
																										'initialise_virtualiser_state_from_relay_store',
																									) && {
																										initialPopulateConnectionArgs:
																											initialisePopulateConnectionArgs,
																									})}
																								/>
																							</CopyPasteContainer>
																						</ColumnPickerContainer>
																					</SelectedRowContainer>
																				</LazyMediaViewContextLoaderContainer>
																			</LazyEditabilityLoaderContainer>
																		</ScrollContainer>
																	</SortChangeContextProvider>
																	<RootConnectionRegister issues={issuesData} />
																	{fg('jira_list_reduce_re_renders') && (
																		<FlatListUpdateHandler
																			onIssueFlatListUpdated={onIssueFlatListUpdated}
																		/>
																	)}
																	{projectData &&
																		expVal(
																			'jira_list_realtime_updates_with_queue',
																			'isEnabled',
																			false,
																		) &&
																		(fg('jira_platform_issue_realtime_updates_used_in_nin') ? (
																			<IssueRealtimeUpdater
																				project={projectData}
																				issues={issuesData}
																				groups={groupData}
																				isHierarchyEnabled={isIssueHierarchyEnabled}
																				isStatusTrackingEnabled={isStatusTrackingEnabled}
																				onRefetch={onRefetch}
																			/>
																		) : (
																			<RealtimeUpdater
																				project={projectData}
																				issues={issuesData}
																				groups={groupData}
																				isHierarchyEnabled={isIssueHierarchyEnabled}
																				isStatusTrackingEnabled={isStatusTrackingEnabled}
																				onRefetch={onRefetch}
																			/>
																		))}
																</ConnectionsListContainer>
															</ElementSizeProvider>
														</MainContainer>
													</DraggableRowsContainer>
												</MaybeHoverPopoverProvider>
											</HierarchyContainer>
										</ShouldUseSSRVirtualizerProvider>
									</SiblingCreateActiveIndexContainer>
								</MaybeInlineCreateActiveIndexContainer>
							</IssueFetchQueryVariables>
						</TableIntersectionObserverContainer>
					</TimelineCustomComponentsProvider>
				</MaybeEventHandlersProvider>
			</FeaturesContainer>
			<FireUiAnalytics actionSubject="screen" action="viewed" actionSubjectId="nativeIssueTable" />
		</ContextualAnalyticsData>
	);
};

const NativeIssueTableWithSelectedIssuesProvider =
	IssueTableSelectedIssuesProvider(NativeIssueTable);

export default NativeIssueTableWithSelectedIssuesProvider;

export const NativeIssueTableEntryPoint = ({ props }: EntryPointProps<{}, {}, Props, {}>) => (
	<NativeIssueTableWithSelectedIssuesProvider {...props} />
);
